/**
 * Карточка «Сёла с наибольшим числом отклонений» (US-04.9.1 FR-3/FR-4):
 * колонки «Село · Население · Отклонений», число отклонений — в цвет
 * градации (snpGrades). Наведение на село показывает перечень его тем
 * (FR-3.2), «Показать остальные N» раскрывает список (FR-3.3), клик по
 * селу с первичными данными переключает территорию (drill-through,
 * FR-4.1).
 *
 * Строки — кнопки в сетке (а не ячейки таблицы): так сохраняются
 * недоступное состояние строки без первичных данных, фокус с клавиатуры
 * и подсказка, а колонки всё равно выровнены с шапкой.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GRADE_TEXT, gradeOf } from './snpGrades';
import { HEAD_NUM, HEAD_TEXT, NoData, WidgetCard, fmt } from './snpTable';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { useKato } from '../../../contexts/KatoContext';
import { cn } from '../../../lib/cn';
import type { SnpVillage } from '../../../types/snpNeeds';

/** Свёрнутый список сёл — первые строки (FR-3.3). */
const COLLAPSED_COUNT = 8;

/** Сетка строки: название · население · отклонения. */
const GRID = 'grid grid-cols-[minmax(0,1fr)_5.5rem_6rem] items-baseline';

interface SnpVillagesProps {
  villages: SnpVillage[];
}

export const SnpVillages = ({ villages }: SnpVillagesProps) => {
  const { t } = useTranslation();
  const { selectedKato, setSelectedKato } = useKato();
  const [expanded, setExpanded] = useState(false);

  const countOf = (village: SnpVillage): number =>
    village.deviationCount ?? village.deviations.length;

  // Бэк отдаёт список уже отсортированным (по числу отклонений, затем по
  // КАТО) — сортировка нужна для мока и на случай иного порядка.
  const sorted = [...villages].sort((a, b) => countOf(b) - countOf(a));
  const shown = expanded ? sorted : sorted.slice(0, COLLAPSED_COUNT);
  const hiddenCount = sorted.length - shown.length;

  if (sorted.length === 0) {
    return (
      <WidgetCard
        testId="snp-villages"
        title={t('analytics.snpNeeds.deviations.villagesTitle')}
      >
        <NoData text={t('common.noData')} />
      </WidgetCard>
    );
  }

  return (
    <WidgetCard
      testId="snp-villages"
      title={t('analytics.snpNeeds.deviations.villagesTitle')}
    >
      <div
        className={cn(GRID, 'border-b border-surface-border pb-1.5')}
        aria-hidden="true"
      >
        <span className={cn(HEAD_TEXT, 'p-0')}>
          {t('analytics.snpNeeds.columns.village')}
        </span>
        <span className={cn(HEAD_NUM, 'p-0')}>
          {t('analytics.snpNeeds.columns.population')}
        </span>
        <span className={cn(HEAD_NUM, 'p-0')}>
          {t('analytics.snpNeeds.columns.deviations')}
        </span>
      </div>

      <TooltipProvider delayDuration={150}>
        <ul>
          {shown.map((village) => {
            const count = countOf(village);
            const grade = gradeOf(count);
            const clickable = village.hasData && village.kato !== null;
            const row = (
              <button
                type="button"
                disabled={!clickable}
                onClick={() => {
                  if (village.kato === null) return;
                  // Кнопка «назад» должна возвращать в точку до
                  // проваливания: раздел и КАТО пишем в query текущей
                  // записи истории, село — новой записью (native
                  // history: state роутера сохраняем).
                  if (selectedKato !== null) {
                    window.history.replaceState(
                      window.history.state,
                      '',
                      `?section=snpNeeds&kato=${selectedKato}`,
                    );
                  }
                  window.history.pushState(
                    window.history.state,
                    '',
                    `?section=snpNeeds&kato=${village.kato}`,
                  );
                  setSelectedKato(village.kato);
                }}
                className={cn(
                  GRID,
                  'w-full border-b border-surface-border py-2 text-left text-sm',
                  clickable
                    ? 'cursor-pointer hover:bg-brand-50/60'
                    : 'cursor-default',
                )}
                data-testid={`snp-village-${village.name}`}
              >
                <span className="flex min-w-0 items-baseline gap-1.5 pr-4">
                  <span
                    className={cn(
                      'truncate font-semibold',
                      !clickable && 'text-text-muted',
                    )}
                  >
                    {village.name}
                  </span>
                  {village.okrug !== null && (
                    <span className="truncate text-xs text-brand-600">
                      · {village.okrug}
                    </span>
                  )}
                  {!village.hasData && (
                    <span className="shrink-0 text-xs text-text-faint">
                      {t('analytics.snpNeeds.deviations.noPrimaryData')}
                    </span>
                  )}
                </span>
                <span className="pl-4 text-right tabular-nums text-text-muted">
                  {village.population !== null ? fmt(village.population) : '—'}
                </span>
                <span
                  className={cn(
                    'pl-4 text-right font-bold tabular-nums',
                    GRADE_TEXT[grade],
                  )}
                >
                  {count}
                </span>
              </button>
            );
            return (
              <li key={village.name}>
                {village.deviations.length > 0 ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{row}</TooltipTrigger>
                    <TooltipContent side="top" align="start">
                      <p className="mb-1 font-semibold">
                        {t('analytics.snpNeeds.deviations.deviations')}
                      </p>
                      <ul className="list-disc pl-4">
                        {village.deviations.map((key) => (
                          <li key={key}>
                            {t(`analytics.snpNeeds.topics.${key}`, {
                              defaultValue: key,
                            })}
                          </li>
                        ))}
                      </ul>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  row
                )}
              </li>
            );
          })}
        </ul>
      </TooltipProvider>

      {/* «Показать остальные N» — на всю ширину карточки: кнопка добирает
          низ виджета, чтобы в колонке не оставалось пустого места. */}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-3 w-full rounded-lg bg-brand-50 py-3 text-sm font-bold uppercase tracking-wide text-brand-600 hover:bg-brand-100"
          data-testid="snp-villages-more"
        >
          {t('analytics.snpNeeds.deviations.showMore', { n: hiddenCount })}
        </button>
      )}
    </WidgetCard>
  );
};
